import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icons } from '@/components/icons/Icons';

interface ResizableSplitterProps {
  ratio: number;
  onRatioChange: (newRatio: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  minRatio?: number;
  maxRatio?: number;
  className?: string;
  containerRef?: React.RefObject<HTMLElement>;
  isCollapsed?: boolean;
  onCollapse?: () => void;
}

export const ResizableSplitter: React.FC<ResizableSplitterProps> = ({
  ratio: _ratio,
  onRatioChange,
  onDragStart,
  onDragEnd,
  minRatio = 0.2,
  maxRatio = 0.8,
  className = '',
  containerRef,
  isCollapsed,
  onCollapse,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      onDragStart?.();

      const startX = e.clientX;
      const startRatio = _ratio;

      const handleMouseMove = (e: MouseEvent) => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          const deltaX = e.clientX - startX;
          const totalWidth = containerRef?.current?.clientWidth || window.innerWidth;
          const newRatio = startRatio + deltaX / totalWidth;
          const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newRatio));
          onRatioChange(clampedRatio);
        });
      };

      const handleMouseUp = () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        setIsDragging(false);
        onDragEnd?.();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [minRatio, maxRatio, onRatioChange, onDragStart, onDragEnd, _ratio]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setIsDragging(true);
      onDragStart?.();

      const startX = e.touches[0].clientX;
      const startRatio = _ratio;

      const handleTouchMove = (e: TouchEvent) => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
          const touch = e.touches[0];
          const deltaX = touch.clientX - startX;
          const totalWidth = containerRef?.current?.clientWidth || window.innerWidth;
          const newRatio = startRatio + deltaX / totalWidth;
          const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newRatio));
          onRatioChange(clampedRatio);
        });
      };

      const handleTouchEnd = () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }

        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        setIsDragging(false);
        onDragEnd?.();
      };

      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    },
    [minRatio, maxRatio, onRatioChange, onDragStart, onDragEnd, _ratio]
  );

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`relative flex-shrink-0 ${isCollapsed ? 'w-0' : 'w-2'} flex flex-col justify-center items-center group z-10 cursor-col-resize select-none bg-transparent ${className}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Pill Handle Style */}
      {!isCollapsed && (
        <div
          className={`w-1 h-8 rounded-full transition-colors duration-200 ${
            isDragging ? 'bg-primary' : 'bg-border dark:bg-border-dark group-hover:bg-primary'
          }`}
        />
      )}

      {/* Collapse/Expand Button */}
      {onCollapse && (
        <button
          className={`absolute z-20 top-1/4 w-6 h-6 rounded-md bg-surface/80 dark:bg-surface-dark/80 border border-border dark:border-border-dark shadow-sm flex items-center justify-center text-surface-onSurface dark:text-surface-onSurfaceDark transition-opacity duration-200 focus:opacity-100 active:scale-95
            ${isCollapsed ? 'opacity-100 mr-10' : 'opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100'}`}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onCollapse();
          }}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <Icons.LeftArrow /> : <Icons.RightArrow />}
        </button>
      )}
    </div>
  );
};
